/**
 * ResolveOffersV2 → 内部模型（§4.2.2）
 *
 * 对齐 IDL ResolveOffersV2Data：activities[] + 扁平 recharges/subscribes（按 activity_id 关联）。
 * 写入顺序：先 upsertActivity，再产出只带 activityId 的 task / session。
 * 失败不入队；未知 activity_id 落 __fallback__ 并埋点告警。
 *
 * 新 type 接入：多数走通用解析；字段特殊时再补分支。
 * 必改 defaults / registry / renderer / ACTIVITY_ID_TO_TYPE，见 docs/弹窗挂件统一管理/接入指南.md
 */

import { type IActivityOfferItemDTO, type IActivitySubscribeItemDTO, type IResolveOffersV2Data } from '@/apis/activity';
import { type IActivityMetaDTO } from '@/apis/commerce';
import { useActivityStore } from '@/store/activity';
import { generateUUID } from '@/utils/device/generate-uuid';

import { ACTIVITY_DEFAULTS, FALLBACK_ACTIVITY_TYPE } from './defaults';
import { trackPopupNoShow } from './track-popup';

import type {
    IActivity,
    IActivityDefaultEntry,
    IActivitySession,
    IQueueTask,
    TActivityPopupType,
    TDisplayTab,
    TKnownActivityPopupType,
} from './types';

export interface ITransformOfferCtx {
    /** 本次拉取归属场景 */
    scene: string;
    /** 本次拉取业务决策（query / activate）；部分活动只允许特定入口入队 */
    action?: string;
}

export interface ITransformOfferItemResult {
    task?: IQueueTask;
    session?: IActivitySession;
}

export interface ITransformOfferBatchResult {
    items: ITransformOfferItemResult[];
}

/**
 * 服务端 activity_id → 前端已知弹窗 type（ACTIVITY_DEFAULTS key）。
 * 新活动接入时在此补映射；未命中则走 __fallback__。
 */
const ACTIVITY_ID_TO_TYPE: Record<string, TKnownActivityPopupType> = {
    h5_recharge_retention_v1: 'recharge_retention',
    h5_subscription_retention_v1: 'subscribe_retention',
    // 假优惠弹窗（display-only，纯 UI 包装不改价）
    h5_fake_discount_popup_v1: 'fake_discount',
};

function resolveDefaults(type: string): { resolvedType: TActivityPopupType; defaults: IActivityDefaultEntry } {
    if (Object.prototype.hasOwnProperty.call(ACTIVITY_DEFAULTS, type) && type !== FALLBACK_ACTIVITY_TYPE) {
        const known = type as TKnownActivityPopupType;
        return { resolvedType: known, defaults: ACTIVITY_DEFAULTS[known] };
    }
    trackPopupNoShow({
        activityId: type || 'unknown',
        reason: 'unknown_type_fallback',
        extra: { pop_up_type: type },
    });
    return {
        resolvedType: type || FALLBACK_ACTIVITY_TYPE,
        defaults: ACTIVITY_DEFAULTS[FALLBACK_ACTIVITY_TYPE],
    };
}

/**
 * 将服务端 activity_id 映射为前端弹窗 type。
 * @param activityId 接口 activities[].activity_id
 */
function mapActivityIdToType(activityId: string): string {
    return ACTIVITY_ID_TO_TYPE[activityId] ?? activityId;
}

function deriveExpireAt(meta: IActivityMetaDTO): number | undefined {
    if (typeof meta.remain_ms === 'number' && meta.remain_ms > 0) {
        return Date.now() + meta.remain_ms;
    }
    if (typeof meta.valid_until === 'number' && meta.valid_until > 0) {
        return meta.valid_until;
    }
    return undefined;
}

function filterProductsForActivity(
    activityId: string,
    recharges: IActivityOfferItemDTO[] | undefined,
    subscribes: IActivitySubscribeItemDTO[] | undefined
): { recharges: IActivityOfferItemDTO[]; subscribes: IActivitySubscribeItemDTO[] } {
    return {
        recharges: (recharges ?? []).filter(item => item.activity_info?.activity_id === activityId),
        subscribes: (subscribes ?? []).filter(item => item.activity_info?.activity_id === activityId),
    };
}

function transformOneActivity(
    meta: IActivityMetaDTO,
    raw: IResolveOffersV2Data,
    ctx: ITransformOfferCtx
): ITransformOfferItemResult {
    const { activity_id: rawActivityId } = meta;
    const activityId = rawActivityId != null ? String(rawActivityId) : '';
    if (!activityId) {
        return {};
    }

    const type = mapActivityIdToType(activityId);
    const { resolvedType, defaults } = resolveDefaults(type);
    const { priority } = defaults;
    const now = Date.now();
    const products = filterProductsForActivity(activityId, raw.recharges, raw.subscribes);

    // 假优惠（fake_discount）只允许在「打开付费墙 / 充值中心」的 query 入口入队
    // （scene=paywall|profile、action=query；假优惠不挂 paywall:activate:，避免与挽留互抢）：
    // 冷启 / 退出挽留等其它入口下发的假优惠任务，会在无付费墙章节上下文被 SceneSync 触发误弹；
    // 该弹窗的领取/收口动效强依赖承接页（reader 付费墙 / /pay）上下文。
    const isWallOpenQuery = ctx.action === 'query' && (ctx.scene === 'paywall' || ctx.scene === 'profile');
    if (type === 'fake_discount' && !isWallOpenQuery) {
        return {};
    }

    // 订阅挽留弹窗必须有对应订阅商品；否则 renderer 会空渲染，且 current 会占住弹窗队列。
    if (activityId === 'h5_subscription_retention_v1' && products.subscribes.length === 0) {
        return {};
    }

    const popupPayload: Record<string, unknown> = {
        ...meta,
        recharges: products.recharges,
        subscribes: products.subscribes,
        preview_duration_sec: raw.preview_duration_sec,
        paypal_support_currency: raw.paypal_support_currency,
        stripe_support_currency: raw.stripe_support_currency,
        server_ts: raw.server_ts,
    };

    // is_activated_this_time：true/缺省 → 弹窗；false → 仅挂件（源 1）
    const showPopup = meta.is_activated_this_time !== false;
    const showWidget = meta.is_activated_this_time === false;

    if (!showPopup && !showWidget) {
        return {};
    }

    const activity: IActivity = {
        activityId,
        type: resolvedType,
        priority,
        expireAt: deriveExpireAt(meta),
        popupPayload,
        widgetPayload: popupPayload,
        trackParams: {
            activity_no: meta.activity_no,
            experiment_bucket: meta.experiment_bucket,
            activity_instance_id: meta.activity_instance_id,
        },
        status: 'active',
        createdAt: now,
    };
    useActivityStore.getState().upsertActivity(activity);

    const result: ITransformOfferItemResult = {};

    if (showPopup) {
        const tabs: TDisplayTab[] = defaults.tabs.length > 0 ? [...defaults.tabs] : [];
        result.task = {
            id: generateUUID(),
            activityId,
            type: resolvedType,
            priority,
            tabs,
            source: 'offer_resolve',
            scene: ctx.scene,
            mutexGroup: defaults.mutexGroup,
            blocksOthers: defaults.blocksOthers,
            openWidgetOnDismiss: defaults.openWidgetOnDismiss,
            createdAt: now,
        };
    }

    if (showWidget) {
        result.session = {
            activityId,
            widgetVisible: true,
            widgetSource: 'cold_start',
            createdAt: now,
        };
    }

    return result;
}

/**
 * 将 ResolveOffersV2 响应转换为内部 task / session 列表。
 * @param raw 接口 data；hit=false 或无 activities → 空列表
 * @param ctx 场景上下文
 * @returns 转换结果列表（单条失败不影响整批）
 */
export function transformOfferResolve(
    raw: IResolveOffersV2Data | null | undefined,
    ctx: ITransformOfferCtx
): ITransformOfferBatchResult {
    try {
        if (!raw || typeof raw !== 'object' || !raw.hit) {
            return { items: [] };
        }

        const activities = raw.activities ?? [];
        if (activities.length === 0) {
            return { items: [] };
        }

        const items = activities.reduce<ITransformOfferItemResult[]>((acc, meta) => {
            try {
                const item = transformOneActivity(meta, raw, ctx);
                if (item.task || item.session) {
                    acc.push(item);
                }
            } catch (error) {
                // 单条隔离：一条脏数据不拖垮整批（§4.2.2）
                console.error('[transformOfferResolve] single activity failed', meta?.activity_id, error);
                trackPopupNoShow({
                    activityId: meta?.activity_id ?? 'transform_error',
                    reason: 'transform_error',
                    extra: { message: error instanceof Error ? error.message : String(error) },
                });
            }
            return acc;
        }, []);

        return { items };
    } catch (error) {
        console.error('[transformOfferResolve] failed', error);
        trackPopupNoShow({
            activityId: 'transform_error',
            reason: 'transform_error',
            extra: { message: error instanceof Error ? error.message : String(error) },
        });
        return { items: [] };
    }
}
